import {AsyncInlineError, tracer} from '@s-ui/decorators'
import {UseCase} from '@s-ui/domain'

import {SellerServicesFactory} from '../../seller/Services/factory.js'
import {GenericUserError} from '../../user/Errors/GenericUserError.js'
import UserServicesFactory from '../../user/Services/factory.js'
import {GetOldestC2bAppraisableUserAdError} from '../Errors/GetOldestC2bAppraisableUserAdError.js'
import {GetUserAdsService} from '../Services/GetUserAdsService.js'

class GetOldestC2bAppraisableUserAdUseCase extends UseCase {
  #config
  #genericUserError
  #getCurrentUserService
  #getInstantOfferByAdService
  #getOldestC2bAppraisableUserAdError
  #getUserAdsService

  static create({config}) {
    return new GetOldestC2bAppraisableUserAdUseCase({config})
  }

  constructor({config}) {
    super()
    this.#config = config
    this.#genericUserError = GenericUserError
    this.#getCurrentUserService = UserServicesFactory.getCurrentUserService({
      config: this.#config
    })
    this.#getInstantOfferByAdService = SellerServicesFactory.getInstantOfferByAdSellerService({config: this.#config})
    this.#getOldestC2bAppraisableUserAdError = GetOldestC2bAppraisableUserAdError
    this.#getUserAdsService = GetUserAdsService.create({config: this.#config})
  }

  #getOldestC2BAppraisableUserAd = async ({userAdsListVO}) => {
    const appraisableAds = userAdsListVO.toJSON().ads.filter(ad => ad?.isAppraisable)

    if (!appraisableAds.length) return

    const orderedByDateAppraisableAds = appraisableAds.sort(
      (a, b) => new Date(a.creationDate) - new Date(b.creationDate)
    )

    for (const appraisableAd of orderedByDateAppraisableAds) {
      const [, instantOfferEntity] = await this.#getInstantOfferByAdService.execute({
        adId: appraisableAd.adId,
        simulate: true
      })

      const instantOfferAppraisal = instantOfferEntity?.toJSON().appraisal?.priceRecommended?.value

      if (instantOfferAppraisal) {
        return {...appraisableAd, instantOfferAppraisal}
      }
    }

    return null
  }

  @tracer({metric: 'GetOldestC2bAppraisableUserAdUseCase#execute'})
  @AsyncInlineError()
  async execute() {
    const userEntity = await this.#getCurrentUserService.execute()

    if (!userEntity)
      return Promise.reject(
        this.#genericUserError.create({message: '[GetOldestC2bAppraisableUserAdUseCase#execute] User must be logged'})
      )

    const [getUserAdsError, userAdsListVO] = await this.#getUserAdsService.execute({
      userEntity
    })

    if (getUserAdsError)
      return Promise.reject(this.#getOldestC2bAppraisableUserAdError.create({message: getUserAdsError.toJSON()}))

    const oldestAppraisableAdd = await this.#getOldestC2BAppraisableUserAd({userAdsListVO})

    if (!oldestAppraisableAdd) {
      return Promise.reject(
        this.#getOldestC2bAppraisableUserAdError.create({
          message: '[GetOldestC2bAppraisableUserAdUseCase#execute] No appraisable ads'
        })
      )
    }

    return oldestAppraisableAdd
  }
}

export const factory = GetOldestC2bAppraisableUserAdUseCase.create
